import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    GridSize,
    IconButton,
    makeStyles,
    Theme,
    Typography,
} from '@material-ui/core'
import { AddOutlined } from '@material-ui/icons'
import clsx from 'clsx'
import React, {
    FC,
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

import { PropertyType } from 'bpm/pages/ProductsPage/ProductDetailsPage/components/ListingDetailsPage'
import listingColors from 'bpm/pages/ProductsPage/ProductDetailsPage/listingColors'

import { trapTabKey } from 'bpm/shared/utils/trapFocus'
import PropertyNameAutoComplete from '../../components/PropertyNameAutoComplete'
import {
    ERROR_DUPLICATE_MASTERSPEC_PROPERTY,
    ERROR_DUPLICATE_PROPERTY,
    ERROR_EMPTY_FIELD,
    HELPER_EMPTY_SUBPROPERTY_ERROR,
    HELPER_GENERIC_SUBPROPERTY_ERROR,
} from '../../constants'
import { PPL_ACTIONS, usePPLContext } from '../../PPLProvider'
import { NonMSSubPropertyType } from '../nonMSTypes'
import NonMSEditableOptionGroup from './NonMSEditableOptionGroup'

const {
    propertiesDivider,
    premiumPrimary,
    premiumSecondary,
    buttonPrimary,
    defaultLight,
    errorColor,
    boxBorder,
    productDetailsText,
    contentEditableToggle,
    contentEditableToggleHoverFill,
} = listingColors

const useStyles = makeStyles((theme: Theme) => ({
    subPropertyNameInput: {
        width: '100%',
        border: 0,
        marginLeft: theme.spacing(0.75),
        marginRight: theme.spacing(0.75),
        padding: theme.spacing(0.75),
        fontWeight: 500,
        backgroundColor: defaultLight,
        textOverflow: 'ellipsis',
        '&::placeholder': {
            color: productDetailsText,
            fontWeight: 500,
        },
        '&.error': {
            borderBottom: `1px solid ${errorColor}`,
            outline: 'none',
        },
        '&:focus': {
            outline: 'none',
            borderBottom: `1px solid ${boxBorder}`,
        },
    },
    addIcon: {
        width: pxToRem(12),
        height: pxToRem(12),
    },
    addPremiumPlusProperty: {
        alignSelf: 'center',
        marginBottom: theme.spacing(1),
        padding: pxToRem(5),
        gap: pxToRem(10),
        width: pxToRem(24),
        height: pxToRem(24),
        color: defaultLight,
        backgroundColor: premiumPrimary,
        border: `1px solid ${premiumPrimary}`,
        borderRadius: pxToRem(100),
        '&:hover': {
            backgroundColor: premiumSecondary,
            border: `1px solid ${premiumSecondary}`,
            cursor: 'pointer',
        },
    },
    addButton: {
        fontSize: theme.spacing(1.5),
        color: buttonPrimary,
        border: 0,
        backgroundColor: defaultLight,
        '&.disabled': {
            color: propertiesDivider,
        },
    },
    subPropertyNameInputContainer: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
    },
    addPropertyHover: {
        width: 'unset',
    },
    optionGroupsContainer: {
        display: 'flex',
    },
    optionGroupsWrapper: {
        flex: 11,
    },
    contentEditableToggleWrapper: {
        flex: 1,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
    contentEditableToggle: {
        margin: theme.spacing(0, 1),
        fontSize: theme.spacing(1.5),
        padding: pxToRem(5),
        gap: pxToRem(10),
        width: pxToRem(24),
        height: pxToRem(24),
        color: contentEditableToggle,
        border: `1px solid ${contentEditableToggle}`,
        borderRadius: pxToRem(100),
        '&:hover': {
            backgroundColor: contentEditableToggleHoverFill,
            border: `1px solid ${contentEditableToggleHoverFill}`,
            color: defaultLight,
            cursor: 'pointer',
        },
    },
    contentEditableToggleActive: {
        color: defaultLight,
        backgroundColor: contentEditableToggle,
        '&:hover': {
            backgroundColor: contentEditableToggleHoverFill,
            border: `1px solid ${contentEditableToggleHoverFill}`,
            cursor: 'pointer',
        },
    },
    invisible: {
        display: 'none',
    },
}))

interface SubPropertyProps {
    index: number
    subProperty: NonMSSubPropertyType
    isPropertyGroupInline: boolean
}

const NonMSEditableSubProperty: FC<SubPropertyProps> = ({
    index,
    subProperty,
    isPropertyGroupInline,
}) => {
    const classes = useStyles()

    const { msProperties, nonMSProperties: state, dispatch } = usePPLContext()
    const [inputWidth, setInputWidth] = useState(3)
    const [isAffixActive, setIsAffixActive] = useState(false)

    const subProperties = useMemo(() => state.editableProperty.subProperties, [
        state.editableProperty.subProperties,
    ])

    const [isAddActive, setIsAddActive] = useState(false)
    const [subPropertyName, setSubPropertyName] = useState(
        subProperty.subPropertyName
    )
    const [errors, setErrors] = useState(state.errors.subPropertyName)

    const msPropertiesKeys = useMemo(
        () =>
            Object.entries(msProperties.properties).reduce(
                (
                    acc: Record<
                        string,
                        Array<Record<string, string | boolean>>
                    >,
                    [propertyName, property]
                ) => {
                    acc[propertyName] = (property as PropertyType).map(
                        (subProp) => ({
                            subPropertyName: subProp.subPropertyName ?? '',
                            propertyId: subProp.propertyId ?? '',
                            isPremiumPlus: !!subProp.isPremiumPlus,
                        })
                    )
                    return acc
                },
                {}
            ),
        [msProperties]
    )

    const hasEmptySubPropName = useMemo(
        () =>
            !!subProperties.some(
                (subProp: Record<string, string | Array<unknown>>) =>
                    subProp.subPropertyName === ''
            ),
        [subProperties]
    )

    const isPrefixEditable = useMemo(
        () =>
            !!state.prefixEditable.length &&
            state.prefixEditable.includes(subProperty.propertyId),
        [state.prefixEditable, subProperty.propertyId]
    )
    const subPropertyContainerRef: RefObject<HTMLDivElement> = useRef(null)

    const tooltipTitle =
        hasEmptySubPropName || !subPropertyName
            ? HELPER_EMPTY_SUBPROPERTY_ERROR
            : state.errors.subPropertyName?.error
            ? HELPER_GENERIC_SUBPROPERTY_ERROR
            : ''

    const handleAddEmptySubProperty = useCallback(() => {
        if (!!tooltipTitle || !!isAddActive) {
            return
        }
        dispatch({ type: PPL_ACTIONS.createNewSubProperty, payload: index })
    }, [dispatch, tooltipTitle, index, isAddActive])

    const toggleContentEditable = useCallback(() => {
        const editableContents = !isPrefixEditable
            ? [
                  ...(state.prefixEditable.length ? state.prefixEditable : []),
                  subProperty.propertyId,
              ]
            : (state.prefixEditable ?? []).filter(
                  (subPropIndex: string) =>
                      subPropIndex !== subProperty.propertyId
              )

        dispatch({
            type: PPL_ACTIONS.setPrefixEditable,
            payload: editableContents ?? [],
        })
    }, [state.prefixEditable, dispatch, subProperty.propertyId])

    const changeInputWidth = useCallback(
        (val) => () => {
            setInputWidth(val)
        },
        [setInputWidth]
    )

    const handleSubPropertyNameChange = useCallback(
        (value) => {
            changeInputWidth(3)
            setErrors({})
            setSubPropertyName(value)

            if (!value) {
                dispatch({
                    type: PPL_ACTIONS.batchNonMSActions,
                    payload: [
                        {
                            type: PPL_ACTIONS.setSubPropertyName,
                            payload: {
                                subPropsIndex: index,
                                subPropertyName: value,
                            },
                        },
                        {
                            type: PPL_ACTIONS.setErrors,
                            payload: {
                                subPropertyName: {
                                    value,
                                    error: ERROR_EMPTY_FIELD,
                                },
                            },
                        },
                    ],
                })
                return
            }

            if (value !== subProperty.subPropertyName) {
                dispatch({
                    type: PPL_ACTIONS.batchNonMSActions,
                    payload: [
                        {
                            type: PPL_ACTIONS.setSubPropertyName,
                            payload: {
                                subPropsIndex: index,
                                subPropertyName: value,
                            },
                        },
                        {
                            type: PPL_ACTIONS.setErrors,
                            payload: {
                                subPropertyName: {},
                            },
                        },
                    ],
                })
            }

            if (
                subProperties.some(
                    (subProp: NonMSSubPropertyType) =>
                        subProp.subPropertyName === value &&
                        subProp.propertyId !== subProperty.propertyId
                ) &&
                value !== ''
            ) {
                dispatch({
                    type: PPL_ACTIONS.setErrors,
                    payload: {
                        subPropertyName: {
                            value: value,
                            error: ERROR_DUPLICATE_PROPERTY,
                        },
                    },
                })
                return
            }

            if (
                msPropertiesKeys[state.editableProperty.propertyName] &&
                msPropertiesKeys[state.editableProperty.propertyName].some(
                    (prop) =>
                        prop.subPropertyName === value &&
                        prop.propertyId !== subProperty.propertyId &&
                        !prop.isPremiumPlus
                )
            ) {
                dispatch({
                    type: PPL_ACTIONS.setErrors,
                    payload: {
                        subPropertyName: {
                            value: value,
                            error: ERROR_DUPLICATE_MASTERSPEC_PROPERTY,
                        },
                    },
                })
            }
        },
        [
            changeInputWidth,
            setErrors,
            setSubPropertyName,
            dispatch,
            index,
            subProperties,
            msPropertiesKeys,
            state.editableProperty.propertyName,
            subProperty.propertyId,
        ]
    )

    useEffect(() => {
        if (
            !!state.optionActive &&
            subProperty.propertyId === state.optionActive &&
            !isAddActive
        ) {
            setIsAddActive(true)
        }
    }, [
        state.optionActive,
        setIsAddActive,
        isAddActive,
        subProperty.propertyId,
    ])

    useEffect(() => {
        if (subPropertyContainerRef.current) {
            subPropertyContainerRef.current?.addEventListener(
                'keydown',
                trapTabKey(subPropertyContainerRef)
            )

            return () => {
                subPropertyContainerRef.current?.removeEventListener(
                    'keydown',
                    trapTabKey(subPropertyContainerRef)
                )
            }
        }
    }, [])

    return (
        <GridContainer>
            <GridItem
                sm={12}
                md={inputWidth as GridSize}
                className={classes.subPropertyNameInputContainer}
            >
                <PropertyNameAutoComplete
                    defaultValue={subPropertyName}
                    type="property"
                    headerText={state.editableProperty.propertyName}
                    onOpen={changeInputWidth(12)}
                    onClose={changeInputWidth(3)}
                    setAutocompleteValue={handleSubPropertyNameChange}
                    errors={errors}
                    setErrors={setErrors}
                />
            </GridItem>
            <GridItem
                ref={subPropertyContainerRef}
                sm={12}
                md={9}
                className={clsx(classes.optionGroupsContainer, {
                    [classes.invisible]: inputWidth === 12,
                })}
            >
                <Typography
                    component="span"
                    className={classes.optionGroupsWrapper}
                >
                    {subProperty.optionGroups.length &&
                        subProperty.optionGroups.map(
                            ({ groupId }, groupIndex) => (
                                <NonMSEditableOptionGroup
                                    key={`${groupId}_${groupId}`}
                                    index={index}
                                    subProperty={subProperty}
                                    groupIndex={groupIndex}
                                    isAffixActive={isAffixActive}
                                    setIsAffixActive={setIsAffixActive}
                                />
                            )
                        )}
                </Typography>

                {!!isPropertyGroupInline && (
                    <Typography
                        component="span"
                        className={classes.contentEditableToggleWrapper}
                    >
                        <IconButton
                            className={clsx(classes.contentEditableToggle, {
                                [classes.contentEditableToggleActive]: !!isPrefixEditable,
                            })}
                            onClick={toggleContentEditable}
                            data-testid="activateText"
                        >
                            t
                        </IconButton>
                    </Typography>
                )}
            </GridItem>
            <GridItem>
                <HoverTooltip text={tooltipTitle} placements={['bottom']}>
                    <Typography
                        component="button"
                        className={clsx(classes.addButton, {
                            disabled: !!tooltipTitle,
                        })}
                        onClick={handleAddEmptySubProperty}
                    >
                        <AddOutlined className={classes.addIcon} /> New Property
                    </Typography>
                </HoverTooltip>
            </GridItem>
        </GridContainer>
    )
}

export default NonMSEditableSubProperty
